import React, { Attributes } from 'react';
import classNames from 'classnames';
import { colors } from 'components/styles/variables';

/**
 * TextField component
 * @param label - label to be displayed above the input
 * @param InputLabelProps - props for customizing the label
 * @param InputProps - props for customizing the input element
 * @param props - other props to be passed to the input element
 */
function TextField({
  label,
  className,
  InputLabelProps = {},
  InputProps = {},
  ...props
}: {
  label?: string;
  className?: string;
  InputLabelProps?: {
    className?: string;
    style?: React.CSSProperties;
    [key: string]: unknown;
  };
  InputProps?: {
    className?: string;
    inputComponent?: React.ElementType<unknown>;
    style?: React.CSSProperties;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}): JSX.Element {
  const { inputComponent, ...restInputProps } = InputProps;
  const id = props?.id ? `${props.id}` : undefined;
  const variant = props.variant === 'standard' ? 'standard' : 'default';
  const inputClassName = classNames(
    'text-field',
    variant === 'standard' ? 'text-field-standard' : 'text-field-default',
    InputProps.className,
    className
  );

  const inputProps = {
    ...restInputProps,
    ...props,
    className: inputClassName,
    style: InputProps.style,
  };

  const inputElement: JSX.Element = inputComponent ? (
    React.createElement(inputComponent, inputProps as Attributes)
  ) : (
    <input {...inputProps} />
  );

  return (
    <>
      {label ? (
        <label
          htmlFor={id}
          className={classNames('label', InputLabelProps.className)}
          style={InputLabelProps.style}
        >
          <span>{label}</span>
          {inputElement}
        </label>
      ) : (
        inputElement
      )}
      <style jsx global>{`
        .label {
          display: block;
          font-size: 14px;
          font-weight: 500;
          margin-bottom: 4px;
          transition: 0.2s;
          line-height: 1.4375em;
          color: rgba(0, 0, 0, 0.6);
        }
        .label:focus-within {
          color: ${colors.BLACK};
        }
        .text-field {
          width: 100%;
          font-weight: 400;
          box-sizing: border-box;
          outline: none;
          color: ${colors.BLACK};
          transition: border-color 0.2s, box-shadow 0.15s ease-in-out;
          display: block;
          max-width: 100%;
        }
        .text-field-standard {
          padding: 4px 0 5px 0;
          margin-bottom: 0;
          font-size: 20px;
          line-height: normal;
          border: none;
          border-bottom: 1px solid ${colors.BLACK};
          border-radius: 0;
          background: transparent;
        }
        .text-field-standard:disabled {
          background: transparent;
          color: ${colors.G3};
        }
        .text-field-default {
          padding: 0.8rem;
          font-size: 1.4rem;
          line-height: 1.5;
          background-color: ${colors.WHITE};
          background-clip: padding-box;
          border: 1px solid ${colors.G2};
          border-radius: 0.25rem;
        }
        .text-field-default:focus {
          color: ${colors.BLACK};
          background-color: ${colors.WHITE};
          border-color: ${colors.G2};
          outline: 0;
        }
      `}</style>
    </>
  );
}

export default TextField;
